import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TutorialStep } from '../tutorial/steps';
import { buildTutorialSteps } from '../tutorial/buildSteps';
import { apiService } from '../services/api';
import {
  beginTutorialSession,
  clearTutorialSession,
  logTutorialProgress,
  type TutorialId,
} from '../services/tutorialProgress';
import { getModuleSteps, getModuleTourId, MODULES_USING_SANDBOX } from '../training/moduleSteps';
import { TUTORIAL_STEP_LAYOUT } from '../tutorial/stepLayout';
import { setActiveProject, setTutorialSandboxKey } from '../hooks/useActiveProject';

/** Registry key for DELETE /projects/{name} — same as create-empty `project_name` (e.g. ABC_minigrid). */
async function deleteTutorialSandboxProject(projectName: string | null | undefined): Promise<void> {
  if (!projectName) return;
  try {
    await apiService.deleteProject(projectName);
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return;
    console.warn('Tutorial: could not delete sandbox project', projectName, e);
  }
}

function randomTutorialProjectCode(): string {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  let s = '';
  for (let i = 0; i < 3; i++) {
    s += letters[Math.floor(Math.random() * letters.length)];
  }
  return s;
}

async function seedSandboxDemandConnections(projectId: string): Promise<void> {
  const lat = -29.3;
  const lon = 28.5;
  const rows: Array<{
    site_number: string;
    connection_type: string;
    latitude: number;
    longitude: number;
    hh_pp: number;
  }> = [];
  for (let i = 0; i < 10; i++) {
    rows.push({
      site_number: `T${i + 1}`,
      connection_type: 'HH',
      latitude: lat + i * 0.0008,
      longitude: lon + i * 0.0008,
      hh_pp: 1,
    });
  }
  for (let i = 0; i < 3; i++) {
    rows.push({
      site_number: `S${i + 1}`,
      connection_type: 'SME',
      latitude: lat + 0.012 + i * 0.0008,
      longitude: lon + 0.01,
      hh_pp: 1,
    });
  }
  rows.push({
    site_number: 'SCH1',
    connection_type: 'SCH',
    latitude: lat + 0.02,
    longitude: lon + 0.02,
    hh_pp: 1,
  });
  await apiService.importSurveyConnections(projectId, rows);
}

async function createTutorialSandbox(
  setSandboxProjectId: (v: string | null) => void,
  setSandboxProjectName: (v: string | null) => void,
  setSandboxVersion: (v: string | null) => void,
  setSandboxCreateError: (v: string | null) => void,
): Promise<{ projectId: string; project_name: string; version: string } | null> {
  try {
    for (let attempt = 0; attempt < 12; attempt++) {
      const code = randomTutorialProjectCode();
      try {
        const res = await apiService.createEmptyProject({
          name: 'Tutorial sandbox',
          code,
          country: 'LS',
          portfolio: 'Tutorial',
        });
        setSandboxProjectId(res.projectId);
        setSandboxProjectName(res.project_name);
        setSandboxVersion(res.version);
        setSandboxCreateError(null);
        setActiveProject(res.projectId, res.project_name);
        return { projectId: res.projectId, project_name: res.project_name, version: res.version };
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 409) continue;
        const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
        console.error('Tutorial sandbox create failed:', e);
        setSandboxCreateError(typeof msg === 'string' ? msg : 'Could not create tutorial project');
        return null;
      }
    }
    setSandboxCreateError('Could not allocate a unique project code for the tutorial.');
  } catch (e: any) {
    console.error('Tutorial sandbox error:', e);
    setSandboxCreateError(e?.message || 'Could not create tutorial project');
  }
  return null;
}

interface TutorialContextType {
  active: boolean;
  /** 0-based index into activeSteps; exposed for UI (e.g. planner tutorial nav hints). */
  currentStep: number;
  /** Index in full platform STEPS (stepConfig.id) — use for gate wiring in pages. */
  platformStepIndex: number;
  stepConfig: TutorialStep;
  totalSteps: number;
  gateOpen: boolean;
  data: Record<string, any>;
  /** Session id for the disposable tutorial sandbox project (uGridPLAN save step). */
  sandboxProjectId: string | null;
  sandboxProjectName: string | null;
  sandboxVersion: string | null;
  sandboxCreateError: string | null;
  /** When set, tour is a training module slice (not full platform pipeline). */
  trainingModuleId: string | null;
  start: () => void;
  startModule: (moduleId: string) => void;
  exit: () => void;
  advance: () => void;
  setGateValue: (key: string, value: any) => void;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

export function TutorialProvider({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const platformSteps = useMemo(
    () => buildTutorialSteps(t).filter((_, i) => !TUTORIAL_STEP_LAYOUT[i]?.moduleOnly),
    [t, i18n.language],
  );
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSteps, setActiveSteps] = useState<TutorialStep[]>(platformSteps);
  const [tourId, setTourId] = useState<TutorialId>('platform');
  const [trainingModuleId, setTrainingModuleId] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, any>>({});
  const [sandboxProjectId, setSandboxProjectId] = useState<string | null>(null);
  const [sandboxProjectName, setSandboxProjectName] = useState<string | null>(null);
  const [sandboxVersion, setSandboxVersion] = useState<string | null>(null);
  const [sandboxCreateError, setSandboxCreateError] = useState<string | null>(null);
  const navigate = useNavigate();
  const sandboxProjectNameRef = useRef<string | null>(null);

  useEffect(() => {
    sandboxProjectNameRef.current = sandboxProjectName;
    // Mirror to localStorage so non-context resolvers (Exclusions / Design Grid /
    // uGridNET modal) target the sandbox while the tour runs (see
    // resolveActiveProjectKey). Cleared when the tour ends or the sandbox resets.
    setTutorialSandboxKey(active && sandboxProjectName ? sandboxProjectName : null);
  }, [sandboxProjectName, active]);

  const stepConfig = activeSteps[currentStep] || activeSteps[0] || platformSteps[0];

  useEffect(() => {
    if (!active) {
      setActiveSteps(platformSteps);
    }
  }, [platformSteps, active]);
  const platformStepIndex = stepConfig?.id ?? currentStep;

  const gateKeyOwnerStep = useMemo(() => {
    const m: Record<string, number> = {};
    activeSteps.forEach((s, i) => {
      if (s.gateKey) m[s.gateKey] = i;
    });
    return m;
  }, [activeSteps]);

  const checkGate = useCallback((step: TutorialStep, d: Record<string, any>): boolean => {
    if (step.gateType === 'manual') return false;
    if (!step.gateKey) return false;
    if (step.gateKey === 'projectSaved' && sandboxCreateError) return true;
    const val = d[step.gateKey];
    if (step.minValue !== undefined) {
      const n = typeof val === 'number' ? val : Number(val);
      return typeof n === 'number' && !Number.isNaN(n) && n >= step.minValue;
    }
    return !!val;
  }, [sandboxCreateError]);

  const gateOpen = checkGate(stepConfig, data);

  useEffect(() => {
    if (!active) return;
    setData((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(next)) {
        const owner = gateKeyOwnerStep[key];
        if (owner !== undefined && owner !== currentStep) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [active, currentStep, gateKeyOwnerStep]);

  const resetSandboxState = useCallback(() => {
    setSandboxProjectId(null);
    setSandboxProjectName(null);
    setSandboxVersion(null);
    setSandboxCreateError(null);
  }, []);

  const startSandbox = useCallback(async (
    previousRegistryName: string | null,
    opts?: { seedDemand?: boolean },
  ) => {
    if (previousRegistryName) {
      await deleteTutorialSandboxProject(previousRegistryName);
    }
    const created = await createTutorialSandbox(
      setSandboxProjectId,
      setSandboxProjectName,
      setSandboxVersion,
      setSandboxCreateError,
    );
    if (created && opts?.seedDemand) {
      try {
        await seedSandboxDemandConnections(created.projectId);
      } catch (e) {
        console.warn('Tutorial: could not seed sandbox connections for 8760', e);
      }
    }
  }, []);

  const start = useCallback(() => {
    const previousRegistryName = sandboxProjectNameRef.current;
    setData({});
    setCurrentStep(0);
    resetSandboxState();
    setActiveSteps(platformSteps);
    setTourId('platform');
    setTrainingModuleId(null);
    setActive(true);

    const sid = beginTutorialSession('platform');
    logTutorialProgress({
      tourId: 'platform',
      event: 'started',
      sessionId: sid,
      stepIndex: 0,
      stepId: '0',
      stepTitle: platformSteps[0]?.title,
    });

    void startSandbox(previousRegistryName);
  }, [resetSandboxState, startSandbox, platformSteps]);

  const startModule = useCallback((moduleId: string) => {
    const steps = getModuleSteps(moduleId, t);
    if (!steps.length) return;
    const tid = getModuleTourId(moduleId) as TutorialId;
    const previousRegistryName = sandboxProjectNameRef.current;

    setData({});
    setCurrentStep(0);
    resetSandboxState();
    setActiveSteps(steps);
    setTourId(tid);
    setTrainingModuleId(moduleId);
    setActive(true);

    const sid = beginTutorialSession(tid);
    logTutorialProgress({
      tourId: tid,
      event: 'started',
      sessionId: sid,
      stepIndex: 0,
      stepId: String(steps[0]?.id),
      stepTitle: steps[0]?.title,
    });

    if (MODULES_USING_SANDBOX.has(moduleId)) {
      void startSandbox(previousRegistryName, { seedDemand: moduleId === 'plant_sizing' });
    }

    // Launching a module ALWAYS navigates to the first step's route. The
    // skipNavigateOnEnter flag is for in-tour step transitions (where the user
    // is already on the right route); honouring it here would strand a launch
    // from My Training on /my-training with nothing to highlight (RCA 2026-06-15).
    const first = steps[0];
    if (first?.route) {
      navigate(first.route);
    }
  }, [navigate, resetSandboxState, startSandbox, t]);

  const exit = useCallback((opts?: { skipProgressLog?: boolean }) => {
    const toDelete = sandboxProjectNameRef.current;
    const wasTrainingModule = trainingModuleId;
    if (active && !opts?.skipProgressLog) {
      const sc = activeSteps[currentStep];
      logTutorialProgress({
        tourId,
        event: 'exited',
        stepIndex: currentStep,
        stepId: sc ? String(sc.id) : undefined,
        stepTitle: sc?.title,
      });
      clearTutorialSession(tourId);
    }
    setActive(false);
    setCurrentStep(0);
    setActiveSteps(platformSteps);
    setTourId('platform');
    setTrainingModuleId(null);
    setData({});
    resetSandboxState();
    sandboxProjectNameRef.current = null;
    void deleteTutorialSandboxProject(toDelete);
    if (wasTrainingModule) {
      navigate('/my-training');
    } else {
      navigate('/');
    }
  }, [navigate, active, currentStep, activeSteps, tourId, trainingModuleId, resetSandboxState, platformSteps]);

  const advance = useCallback(() => {
    const sc = activeSteps[currentStep];
    if (sc.gateType === 'auto' && !checkGate(sc, data)) {
      return;
    }
    logTutorialProgress({
      tourId,
      event: 'step_completed',
      stepIndex: currentStep,
      stepId: String(sc.id),
      stepTitle: sc.title,
    });
    const next = currentStep + 1;
    if (next >= activeSteps.length) {
      logTutorialProgress({
        tourId,
        event: 'completed',
        stepIndex: currentStep,
        stepId: String(sc.id),
        stepTitle: sc.title,
      });
      clearTutorialSession(tourId);
      exit({ skipProgressLog: true });
      return;
    }
    setCurrentStep(next);
    const nextStep = activeSteps[next];
    if (nextStep.route && !nextStep.skipNavigateOnEnter) {
      navigate(nextStep.route);
    }
  }, [currentStep, data, checkGate, exit, navigate, activeSteps, tourId]);

  const setGateValue = useCallback((key: string, value: any) => {
    setData((prev) => ({ ...prev, [key]: value }));
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        active,
        currentStep,
        platformStepIndex,
        stepConfig,
        totalSteps: activeSteps.length,
        gateOpen,
        data,
        sandboxProjectId,
        sandboxProjectName,
        sandboxVersion,
        sandboxCreateError,
        trainingModuleId,
        start,
        startModule,
        exit,
        advance,
        setGateValue,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used within TutorialProvider');
  return ctx;
}
